import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  whatsappTemplate, linkedinTemplate, emailTemplate,
} from "../../lib/share-templates";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Copy, Download, ExternalLink, Linkedin, Mail, MessageCircle, Loader2,
} from "lucide-react";

const copy = async (t) => {
  try { await navigator.clipboard.writeText(t); return true; } catch { return false; }
};

/**
 * CampaignShareDialog — the primary action from a campaign card.
 * Reuses the exact same share templates + interactions as the onboarding
 * Step 5 "Live" screen so consultants see one consistent share pattern.
 */
export default function CampaignShareDialog({ open, onOpenChange, campaign }) {
  const { user } = useAuth();
  const [qr, setQr] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);

  useEffect(() => {
    if (!open || !campaign?.id) return;
    setLoadingQr(true);
    api.get(`/consultants/me/share-links/${campaign.id}/qr`)
      .then((r) => setQr(r.data))
      .catch(() => setQr(null))
      .finally(() => setLoadingQr(false));
  }, [open, campaign?.id]);

  if (!campaign) return null;
  const url = campaign.url || "";

  const onCopyLink = async () => {
    const ok = await copy(url);
    ok ? toast.success("Link copied") : toast.error("Couldn't copy — please copy manually.");
  };
  const onDownloadQr = () => {
    if (!qr?.qr_data_url) return;
    const a = document.createElement("a");
    a.href = qr.qr_data_url;
    a.download = `${(campaign.name || "campaign").replace(/\s+/g, "-").toLowerCase()}-qr.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("QR downloaded");
  };
  const onLinkedIn = async () => {
    const ok = await copy(linkedinTemplate(user, campaign, url));
    if (ok) { toast.success("LinkedIn post copied — opening…"); window.open("https://www.linkedin.com/feed/?shareActive=true", "_blank", "noopener"); }
    else toast.error("Couldn't copy — please copy manually.");
  };
  const onWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(whatsappTemplate(user, campaign, url))}`, "_blank", "noopener");
  const onEmail = () => {
    const { subject, body } = emailTemplate(user, campaign, url);
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="campaign-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share &ldquo;{campaign.name}&rdquo;</DialogTitle>
          <DialogDescription>
            Copy, scan, or use a ready-made post to send this campaign to prospects.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 rounded-lg border border-border/60 bg-secondary/40 px-3 py-2 break-all text-xs font-mono text-muted-foreground" data-testid="share-dialog-url">
          {url}
        </div>

        <div className="mt-3 flex gap-2 flex-wrap">
          <Button onClick={onCopyLink} data-testid="share-dialog-copy"><Copy className="w-4 h-4 mr-2" /> Copy link</Button>
          <Button variant="outline" onClick={() => window.open(url, "_blank", "noopener")} data-testid="share-dialog-preview">
            <ExternalLink className="w-4 h-4 mr-2" /> Preview as business leader
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button type="button" onClick={onLinkedIn} className="flex items-center gap-3 rounded-lg border border-border/60 hover:border-accent/50 bg-card/60 p-3 text-left transition" data-testid="share-dialog-linkedin">
            <div className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0"><Linkedin className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="text-sm font-sans-ui font-semibold">LinkedIn post</div>
              <div className="text-xs text-muted-foreground truncate">Copies + opens LinkedIn</div>
            </div>
          </button>
          <button type="button" onClick={onWhatsApp} className="flex items-center gap-3 rounded-lg border border-border/60 hover:border-accent/50 bg-card/60 p-3 text-left transition" data-testid="share-dialog-whatsapp">
            <div className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0"><MessageCircle className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="text-sm font-sans-ui font-semibold">WhatsApp message</div>
              <div className="text-xs text-muted-foreground truncate">Opens WhatsApp draft</div>
            </div>
          </button>
          <button type="button" onClick={onEmail} className="flex items-center gap-3 rounded-lg border border-border/60 hover:border-accent/50 bg-card/60 p-3 text-left transition" data-testid="share-dialog-email">
            <div className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0"><Mail className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="text-sm font-sans-ui font-semibold">Email invitation</div>
              <div className="text-xs text-muted-foreground truncate">Opens your mail app</div>
            </div>
          </button>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <div className="text-xs text-muted-foreground">
            Scan the QR at events, print on business cards, or drop into slide decks.
          </div>
          <div className="flex flex-col items-center gap-1 shrink-0">
            {loadingQr ? (
              <div className="w-24 h-24 flex items-center justify-center border border-border/60 rounded-md bg-card/40"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            ) : qr?.qr_data_url ? (
              <img src={qr.qr_data_url} alt="Campaign QR" className="w-24 h-24 rounded-md border border-border/60 bg-white p-1.5" data-testid="share-dialog-qr" />
            ) : null}
            {qr?.qr_data_url && (
              <button type="button" onClick={onDownloadQr} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid="share-dialog-download-qr">
                <Download className="w-3 h-3" /> Download QR
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
